import { injectable, interfaces } from 'inversify';
import { action, IObservableArray, makeObservable, observable } from 'mobx';
import { IPagingInfoWithOrder, PaginationInfoDefault } from '../../models/paginationInfo.model';
import { ITeamMember } from '../team/models/TeamMember';
import { Plan } from '../user/user.store';

type Unencrypted = { encrypted: false; encryptionAllowed: boolean };
type Encrypted = { encrypted: true; checksum: string; encryptionAllowed: boolean };

export type EncryptionMode = Unencrypted | Encrypted;

export enum WorkspaceRole {
  None = 'none',
  Owner = 'Owner',
  Manager = 'Manager',
  Trainer = 'Trainer',
  Developer = 'Developer',
  Collaborator = 'Collaborator',
  Guest = 'Guest',
  Worker = 'Worker',
  Assistant = 'Assistant',
}

export interface IWorkspace {
  name: string;
  id: string;
  role: WorkspaceRole;
  locked: boolean;
  timeZoneOffset: number;
  ownerEmail: string;
  ownerPlanTier: Plan;
  encryption: EncryptionMode;
  ownerPlanInfo: {
    plan: Plan;
    limits: {
      maxWorkspaces: number;
      maxWorkspaceUsers: number;
      maxStorageInGB: number;
    };
    usedLimits: {
      usedWorkspaces: number;
      usedWorkspaceUsers: number;
      usedStorageInGB: number;
      workspaceLimitReached: boolean;
      workspaceUsersLimitReached: boolean;
      availableSpaceLimitReached: boolean;
    };
  };
}

interface IWorkspaceMember {
  id: string;
  name: string;
  email: string;
  role: WorkspaceRole;
  hasAccount: boolean;
  submitted: number;
  accepted: number;
  acceptedAfterEdit: number;
  rejected: number;
  reviewed: number;
  edited: number;
}

export interface IWorkspaceListItem {
  id: string;
  name: string;
  role: string;
  createdDate: Date;
  owner: string;
  usersAndWorkersCount: IUsersAndWorkersCount;
  datasetsCount: number;
  imagesCount: number;
  projectsCount: number;
  isOwner: boolean;
  members: ITeamMember[];
  isEncrypted: boolean;
  isLocked: boolean;
  timeZoneOffset: number;
}

interface IUsersAndWorkersCount {
  usersCount: number;
  workersCount: number;
}

export interface ILimitInfo {
  targetLimit: number;
  targetLimitUsage: number;
  workspaceLimitUsage: number;
}

export interface ITakeOverWorkspaceLimits {
  workspaces: ILimitInfo;
  users: ILimitInfo;
  storage: ILimitInfo;
  canEncrypt: boolean;
  isAllowed: boolean;
}

export interface IWorkspacesStore {
  // permissions
  canAddWorkspace: boolean;

  // list
  workspaces: IObservableArray<IWorkspace>;
  workspaceListItems: IObservableArray<IWorkspaceListItem>;
  showLoader: boolean;
  workspacesPaging: IPagingInfoWithOrder;

  // search
  workspaceNameSearch: string;
  workspaceOwnerSearch: string;

  // current
  readonly preferredWorkspaceId: string | undefined;
  takeOverWorkspaceLimits: ITakeOverWorkspaceLimits | undefined;
  currentWorkspaceMembers: IObservableArray<IWorkspaceMember>;

  addWorkspace(id: string, name: string, plan: Plan, timeZone: number, ownerEmail: string, checksum?: string): void;
  removeWorkspace(id: string): void;
  getPreferredOrDefaultWorkspaceId(): string | undefined;
  setForceRefresh(value: boolean): void;
  setPreferredWorkspace(workspaceId: string): void;
  setWorkspaceMembers(teamMembers: IWorkspaceMember[]): void;
}

export namespace IWorkspacesStore {
  export const $: interfaces.ServiceIdentifier<IWorkspacesStore> = Symbol('WORKSPACE_STORE');
}

@injectable()
export class WorkspacesStore implements IWorkspacesStore {
  workspaces: IObservableArray<IWorkspace> = observable.array([]);

  currentWorkspaceMembers: IObservableArray<IWorkspaceMember> = observable.array([]);

  workspacesPaging: IPagingInfoWithOrder = {
    ...PaginationInfoDefault,
    orderBy: 'name',
    orderType: 'asc',
  };

  workspaceListItems: IObservableArray<IWorkspaceListItem> = observable.array([]);

  showLoader: boolean = false;

  workspaceNameSearch: string = '';

  workspaceOwnerSearch: string = '';

  canAddWorkspace: boolean = false;

  takeOverWorkspaceLimits: ITakeOverWorkspaceLimits | undefined = undefined;

  preferredWorkspaceId: string | undefined;

  forceRefresh: boolean = false;

  constructor() {
    makeObservable(this, {
      workspaces: observable,
      currentWorkspaceMembers: observable,
      workspacesPaging: observable,
      workspaceListItems: observable.shallow,
      showLoader: observable,
      workspaceNameSearch: observable,
      workspaceOwnerSearch: observable,
      canAddWorkspace: observable,
      takeOverWorkspaceLimits: observable,
      preferredWorkspaceId: observable,
      forceRefresh: observable,
      addWorkspace: action,
      removeWorkspace: action,
      setWorkspaceMembers: action,
      getPreferredOrDefaultWorkspaceId: action,
      setForceRefresh: action,
      setPreferredWorkspace: action,
    });
  }

  addWorkspace(id: string, name: string, plan: Plan, timeZoneOffset: number, ownerEmail: string, checksum?: string): void {
    this.workspaces.push({
      id,
      name,
      timeZoneOffset,
      ownerEmail,
      role: WorkspaceRole.Owner,
      locked: false,
      ownerPlanTier: plan,
      encryption: checksum ? { checksum, encrypted: true, encryptionAllowed: true } : { encrypted: false, encryptionAllowed: true },
      ownerPlanInfo: {
        plan,
        limits: { maxWorkspaces: 0, maxWorkspaceUsers: 0, maxStorageInGB: 0 },
        usedLimits: {
          usedWorkspaces: 0,
          usedWorkspaceUsers: 0,
          usedStorageInGB: 0,
          workspaceLimitReached: false,
          workspaceUsersLimitReached: false,
          availableSpaceLimitReached: false,
        },
      },
    });
  }

  removeWorkspace(id: string): void {
    this.workspaces.replace(
      this.workspaces.filter((w: IWorkspace) => {
        return w.id !== id;
      })
    );
  }

  setWorkspaceMembers(teamMembers: IWorkspaceMember[]): void {
    this.currentWorkspaceMembers.replace(teamMembers);
  }

  getPreferredOrDefaultWorkspaceId(): string | undefined {
    if (this.workspaces.some((x) => x.id === this.preferredWorkspaceId)) {
      return this.preferredWorkspaceId;
    }
    if (this.workspaces.length) {
      return this.workspaces[0].id;
    }

    return undefined;
  }

  setForceRefresh(value: boolean): void {
    this.forceRefresh = value;
  }

  setPreferredWorkspace(workspaceId: string): void {
    this.preferredWorkspaceId = workspaceId;
  }
}
